import { useEffect, useRef } from "react";
import {
  useElementScrollRestoration,
  useLocation,
  useRouter,
} from "@tanstack/react-router";

const HOLD_TIMEOUT_MS = 10_000;
const USER_INPUT_EVENTS = ["wheel", "touchstart", "keydown", "pointerdown"];
const HISTORY_TRAVERSALS = new Set(["BACK", "FORWARD", "GO"]);

function holdScrollPosition(scrollX: number, scrollY: number): () => void {
  const content = document.getElementById("root") ?? document.body;
  const apply = () => {
    if (window.scrollX !== scrollX || window.scrollY !== scrollY) {
      window.scrollTo(scrollX, scrollY);
    }
  };
  const observer = new ResizeObserver(apply);

  const stop = () => {
    observer.disconnect();
    clearTimeout(timer);
    for (const type of USER_INPUT_EVENTS) removeEventListener(type, stop);
  };
  const timer = setTimeout(stop, HOLD_TIMEOUT_MS);

  for (const type of USER_INPUT_EVENTS) {
    addEventListener(type, stop, { passive: true });
  }
  observer.observe(content);
  apply();

  return stop;
}

export function KeepRestoredScroll(): null {
  const router = useRouter();
  const locationKey = useLocation({
    select: (location) => location.state.__TSR_key ?? location.href,
  });
  const saved = useElementScrollRestoration({ getElement: () => window });
  const lastHistoryAction = useRef<string | undefined>(undefined);

  useEffect(
    () =>
      router.history.subscribe(({ action }) => {
        lastHistoryAction.current = action.type;
      }),
    [router],
  );

  useEffect(() => {
    const action = lastHistoryAction.current;
    if (!saved || !action || !HISTORY_TRAVERSALS.has(action)) return;

    return holdScrollPosition(saved.scrollX, saved.scrollY);
  }, [locationKey, saved]);

  return null;
}
